import { adaptWalletAnalysisResponse } from '@modules/consolidator/utils/adapt-wallet-analysis-response/adapt-wallet-analysis-response'
import { toNumberOrNull } from '@modules/consolidator/utils/to-number-or-null/to-number-or-null'

/**
 * Chaves do bloco `avisos`. O backend sempre manda as cinco (array vazio quando
 * não há aviso) — a UI nunca precisa de `?.`, que o Babel do projeto não
 * transpila.
 */
export const GROUP_AVISOS_KEYS = [
  'membro_sem_ativos',
  'conta_conjunta',
  'frescor_membro',
  'benchmark_divergente',
  'modo_degradado',
]

/**
 * @returns {Object} Bloco de avisos vazio — as cinco chaves como array.
 */
export const buildEmptyGroupAvisos = () => {
  const avisos = {}
  for (const key of GROUP_AVISOS_KEYS) {
    avisos[key] = []
  }
  return avisos
}

/**
 * Adapta a resposta da consolidação por grupo familiar.
 *
 * O payload do grupo é um SUPERSET aditivo do payload por cliente: os blocos
 * `portfolio`, `breakdown`, `statistics`, `filters` etc. têm exatamente o mesmo
 * shape. Por isso reaproveitamos `adaptWalletAnalysisResponse` sem alteração
 * (inclusive a sanitização de NaN) e apenas ANEXAMOS os blocos novos do grupo,
 * que aquele adapter — por ser do contrato por cliente — não conhece.
 *
 * ★ Nenhuma estatística é calculada, somada ou ponderada aqui. Rentabilidade,
 * volatilidade e Sharpe chegam prontos do backend, que os calcula sobre a união
 * dos ativos dos membros. Agregar por membro no cliente erraria a rentabilidade
 * (ignora a covariância entre as carteiras).
 *
 * @param {Object} response Envelope `{status, message, value}` do backend.
 * @returns {Object} Resposta adaptada + blocos do grupo.
 */
export const adaptGroupWalletAnalysisResponse = (response) => {
  const adapted = adaptWalletAnalysisResponse(response)
  const value = extractGroupValue(response)

  return {
    ...adapted,
    // Discriminadores dos estados de UI. O par (status, shape de value) é o que
    // separa "grupo sem membros" (404 sem value) de "nenhum membro cotizado"
    // (404 com esqueleto) — não existe campo de código de erro no contrato.
    has_payload: value !== null,
    has_cotized_assets: Boolean(value && value.has_cotized_assets),
    max_assets: toNumberOrNull(value && value.max_assets),
    assets_count: toNumberOrNull(value && value.assets_count),
    membros_bloqueantes: toArrayOrEmpty(value && value.membros_bloqueantes),
    // Blocos aditivos do grupo.
    grupo: extractObjectOrNull(value && value.grupo),
    membros: toArrayOrEmpty(value && value.membros),
    frescor_por_membro: toArrayOrEmpty(value && value.frescor_por_membro),
    avisos: normalizeGroupAvisos(value && value.avisos),
  }
}

const extractGroupValue = (response) => {
  const value = response && response.value
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null
}

const extractObjectOrNull = (candidate) =>
  candidate && typeof candidate === 'object' && !Array.isArray(candidate)
    ? candidate
    : null

const toArrayOrEmpty = (candidate) =>
  Array.isArray(candidate) ? candidate : []

export const normalizeGroupAvisos = (avisos) => {
  const normalized = buildEmptyGroupAvisos()
  if (!avisos || typeof avisos !== 'object') return normalized

  for (const key of GROUP_AVISOS_KEYS) {
    normalized[key] = toArrayOrEmpty(avisos[key])
  }
  return normalized
}
